import { useEffect, useReducer } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";

import { apiFetch, VendorUtilities } from "@/utilities/VendorUtilities";
import { CookieStore } from "@/services/CookieStore";
import { getTeamAndUserData, setLoadingToFalse } from "@/redux/auth/sessions/actions";
import { SessionManager } from "@/services/SessionManager";
import Alert from "@/components/shared/Alert";
import SwitchInput from "@/components/shared/SwitchInput";
import TextInput from "@/components/shared/TextInput";

import { Button } from "../shared/Button";

const CompleteSignup = () => {
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      firstName: "",
      lastName: "",
      company: "",
      autojoinEnabled: false
    }
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.auth.sessions.sessionData.user);
  const team = useSelector(state => state.auth.sessions.sessionData.team);
  const {
    mutate: updateAccountInformation,
    loading: updateAccountLoading,
    error: updateAccountError
  } = useMutation({
    mutationFn: async payload => {
      const path = `/user/update/${user.id}`;
      return (
        await apiFetch(path, {
          endpoint: SessionManager.getApiEndpoint(),
          method: "PUT",
          body: JSON.stringify(payload)
        })
      ).json();
    },
    retry: false,
    onSuccess: () => {
      updateTeamInformation({
        name: state.company.trim(),
        team_autojoin_enabled: state.autojoinEnabled
      });
    }
  });
  const {
    mutate: updateTeamInformation,
    loading: updateTeamLoading,
    error: updateTeamError
  } = useMutation({
    mutationFn: async payload => {
      const path = "/team";
      return (
        await apiFetch(path, {
          endpoint: SessionManager.getApiEndpoint(),
          method: "PUT",
          body: JSON.stringify(payload)
        })
      ).json();
    },
    retry: false,
    onSuccess: () => {
      if (CookieStore.getItem("team-selection")) {
        navigate("/team-selection");
      } else {
        navigate("/apps");
      }
    }
  });

  const handleAutojoinToggle = async () => {
    setState({
      autojoinEnabled: !state.autojoinEnabled
    });
  };

  const handleFormChange = (field, value) => {
    let nextState = {};
    nextState[field] = value;
    setState(nextState);
  };

  const onSubmit = e => {
    e.preventDefault();

    updateAccountInformation({
      firstname: state.firstName.trim(),
      lastname: state.lastName.trim()
    });
  };

  useEffect(() => {
    VendorUtilities.purgeVOneData();
    dispatch(setLoadingToFalse());
    dispatch(getTeamAndUserData());
  }, []);

  useEffect(() => {
    if (user.firstname !== state.firstName || user.lastname !== state.lastName) {
      setState({
        firstName: user.firstname || "",
        lastName: user.lastname || ""
      });
    }
  }, [user]);

  useEffect(() => {
    if (team && !team.needs_profile) {
      navigate("/apps");
    }
  }, [team]);

  return (
    <div className="w-full px-5 mx-auto box-border flex flex-col flex-1 min-h-0 items-center justify-center bg-color--teal h-full overflow-y-auto">
      <div className="flex flex-col items-center lg:flex-row gap-4 lg:gap-0 flex-auto w-full CreateApp-wrapper my-6">
        <div className="flex-1 min-h-0 items-center justify-center illustration-wrapper hidden sm:flex sm:flex-col">
          <div className="left-content-wrap flex flex-col justify-center items-center">
            <span className="icon u-createTeam"></span>
            <div className="mt-3">
              <p className="text-base font-normal text-gray-600 leading-[1.4] mt-3">
                Your account is ready, but to use Replicated you'll need to create a
                Team.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-1 gap-8">
          <div className="right-content-wrap flex flex-col gap-8 box-border">
            <div>
              <h1 className="text-indigo-500 leading-[1.4]">
                Create your team
              </h1>
              <p className="text-base font-normal text-gray-600 leading-[1.4] mt-3">
                Before we get started, let's get some information about you and your
                team.
              </p>
            </div>
            <div className="CreateAppPlatform--formWrapper">
              <div className="auth-pages flex flex-col gap-4">
                {updateAccountError && (
                  <Alert variant="error" className="!m-0 !p-4">
                    {updateAccountError?.message}
                  </Alert>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first-name">First Name</label>
                    <TextInput
                      name="first-name"
                      type="text"
                      placeholder="Jack"
                      size="20"
                      value={state.firstName}
                      onChangeValue={value => handleFormChange("firstName", value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="last-name">Last Name</label>
                    <TextInput
                      name="last-name"
                      type="text"
                      placeholder="Shephard"
                      size="20"
                      value={state.lastName}
                      onChangeValue={value => handleFormChange("lastName", value)}
                    />
                  </div>
                </div>
                <div>
                  {updateTeamError && (
                    <Alert variant="error" className="!m-0 !p-4">
                      {updateTeamError?.message}
                    </Alert>
                  )}
                  <label htmlFor="company-name">Company</label>
                  <TextInput
                    name="company-name"
                    type="text"
                    placeholder="Company Name"
                    value={state.company}
                    onChangeValue={value => handleFormChange("company", value)}
                  />
                </div>
                {user?.has_private_email_domain && (
                  <div className="mt-2 flex flex-col gap-2">
                    <SwitchInput
                      label="Enable Team Auto-join"
                      checked={state.autojoinEnabled}
                      onChange={handleAutojoinToggle}
                    />
                    <p className="text-gray-600 text-sm">
                      When team auto-join is enabled, anyone with a validated{" "}
                      <span className="font-semibold">
                        {user?.email?.split("@")[1]}
                      </span>{" "}
                      email will be able to join this team. The default policy assigned
                      is <span className="font-semibold">Read Only</span> - you can
                      change the default on the{" "}
                      <Link
                        target="_blank"
                        to="/team/autojoin"
                        className="text-blue-300 hover:underline font-semibold"
                      >
                        Auto-join settings page
                      </Link>
                      .
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col right-content-wrap">
            <div className="FormButton-wrapper flex">
              <Button kind="primary-purple" size="large" onClick={onSubmit}>
                {updateAccountLoading || updateTeamLoading ? "Loading..." : "Continue"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteSignup;
